import React, { useState, useEffect } from 'react';
import { PageState } from '../types';
import {
    HomeIcon,
    UserIcon,
    UsersIcon,
    GoodieWhiteIcon,
    MailIcon,
    SettingsIcon,
    LogoutIcon,
    VolumeHighIcon,
    BellIcon,
    ArrowUpIcon,
    ChevronLeftIcon,
    SparklesIcon,
    AudioIcon,
    PlayIcon,
    PauseIcon,
    PhoneIcon
} from './Icons';
import { getActiveMods } from '../utils/ModRegistry';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { updateUserOnServer } from '../api';
import { copyToClipboard, readFromClipboard } from '../utils/helpers';

interface NavBarProps {
    page: PageState;
    setPage: (page: PageState) => void;
    currentUserId: string | null;
    hasUnreadMessages: boolean;
    hasUnreadNotifications: boolean;
    onSettingsClick: () => void;
    isDemo?: boolean;
    onLogout: () => void;
    activeVoiceData?: { communityId: string, channelId: string } | null;
    columns?: number;
    onToggleColumns?: () => void;
    role?: string;
    uiScale?: number;
    isIos?: boolean;
    canGoBack?: boolean;
    onBack?: () => void;
    activeMods?: string[];
    pinnedModId?: string;
    onOpenModLibrary?: () => void;
    usersData?: any;
    onOpenAccountSwitcher?: () => void;
    backButtonContent?: React.ReactNode;
    isPeeking?: boolean;
    onTogglePeek?: () => void;
    isShakingMode?: boolean;
    onToggleShakingMode?: () => void;
    onOpenCommunitiesModal?: () => void;
    postcardGap?: number;
    setPostcardGap?: (gap: number) => void;
}

export const NavBar = ({
    page,
    setPage,
    currentUserId,
    hasUnreadMessages,
    hasUnreadNotifications,
    onSettingsClick,
    isDemo = false,
    onLogout,
    activeVoiceData,
    columns = 1,
    onToggleColumns,
    role,
    uiScale,
    isIos = false,
    canGoBack = false,
    onBack,
    activeMods = [],
    pinnedModId = 'daily_footprint',
    onOpenModLibrary,
    usersData = {},
    onOpenAccountSwitcher,
    backButtonContent,
    isPeeking = false,
    onTogglePeek,
    isShakingMode = false,
    onToggleShakingMode,
    onOpenCommunitiesModal,
    postcardGap,
    setPostcardGap
}: NavBarProps) => {
    const auth = useAuth() || {};
    const {
        activeAudioPost,
        audioIsPlaying,
        audioPlayerOpen,
        setAudioPlayerOpen,
        currentUser,
        playAudioPost
    } = auth;

    const currentUserData = currentUserId ? usersData[currentUserId] : null;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';

    const [isScrolled, setIsScrolled] = useState(false);
    const spacingModeRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isShakingMode) return;
        const handleClickOutside = (e: MouseEvent) => {
            const targetButton = (e.target as HTMLElement).closest('button');
            if (targetButton && targetButton.getAttribute('title') === 'Home') {
                return;
            }
            if (spacingModeRef.current && !spacingModeRef.current.contains(e.target as Node)) {
                if (onToggleShakingMode) onToggleShakingMode();
            }
        };
        const timer = setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
        }, 100);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isShakingMode, onToggleShakingMode]);

    const longPressTimer = React.useRef<NodeJS.Timeout | null>(null);
    const wasLongPress = React.useRef(false);

    const handleTouchStart = () => {
        wasLongPress.current = false;
        longPressTimer.current = setTimeout(() => {
            if (onOpenModLibrary) {
                wasLongPress.current = true;
                onOpenModLibrary();
                if ('vibrate' in navigator) navigator.vibrate(50);
            }
        }, 600);
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const profileLongPressTimer = React.useRef<NodeJS.Timeout | null>(null);
    const wasProfileLongPress = React.useRef(false);

    const handleProfileTouchStart = () => {
        wasProfileLongPress.current = false;
        profileLongPressTimer.current = setTimeout(() => {
            if (onOpenAccountSwitcher) {
                wasProfileLongPress.current = true;
                onOpenAccountSwitcher();
                if ('vibrate' in navigator) navigator.vibrate(50);
            }
        }, 600);
    };

    const handleProfileTouchEnd = () => {
        if (profileLongPressTimer.current) {
            clearTimeout(profileLongPressTimer.current);
            profileLongPressTimer.current = null;
        }
    };

    const homeLongPressTimer = React.useRef<NodeJS.Timeout | null>(null);
    const wasHomeLongPress = React.useRef(false);

    const handleHomeTouchStart = () => {
        wasHomeLongPress.current = false;
        homeLongPressTimer.current = setTimeout(() => {
            if (onToggleShakingMode) {
                wasHomeLongPress.current = true;
                onToggleShakingMode();
                if ('vibrate' in navigator) navigator.vibrate(50);
            }
        }, 600);
    };

    const handleHomeTouchEnd = () => {
        if (homeLongPressTimer.current) {
            clearTimeout(homeLongPressTimer.current);
            homeLongPressTimer.current = null;
        }
    };

    const communitiesLongPressTimer = React.useRef<NodeJS.Timeout | null>(null);
    const wasCommunitiesLongPress = React.useRef(false);

    const handleCommunitiesTouchStart = () => {
        wasCommunitiesLongPress.current = false;
        communitiesLongPressTimer.current = setTimeout(() => {
            if (onOpenCommunitiesModal) {
                wasCommunitiesLongPress.current = true;
                onOpenCommunitiesModal();
                if ('vibrate' in navigator) navigator.vibrate(50);
            }
        }, 600);
    };

    const handleCommunitiesTouchEnd = () => {
        if (communitiesLongPressTimer.current) {
            clearTimeout(communitiesLongPressTimer.current);
            communitiesLongPressTimer.current = null;
        }
    };

    const settingsLongPressTimer = React.useRef<NodeJS.Timeout | null>(null);
    const wasSettingsLongPress = React.useRef(false);
    const [showQuickAppearance, setShowQuickAppearance] = useState(false);
    const quickAppearanceRef = React.useRef<HTMLDivElement>(null);

    const handleSettingsTouchStart = () => {
        wasSettingsLongPress.current = false;
        settingsLongPressTimer.current = setTimeout(() => {
            wasSettingsLongPress.current = true;
            setShowQuickAppearance(true);
            if ('vibrate' in navigator) navigator.vibrate(50);
        }, 600);
    };

    const handleSettingsTouchEnd = () => {
        if (settingsLongPressTimer.current) {
            clearTimeout(settingsLongPressTimer.current);
            settingsLongPressTimer.current = null;
        }
    };

    const [activeDragButton, setActiveDragButton] = useState<string | null>(null);
    const [dragDeltaY, setDragDeltaY] = useState<number>(0);
    const dragStartYRef = React.useRef<number>(0);
    const hasTriggeredDragRef = React.useRef<boolean>(false);

    const triggerHoldAction = (buttonId: string) => {
        if ('vibrate' in navigator) navigator.vibrate(50);
        switch (buttonId) {
            case 'Home':
                if (onToggleShakingMode) onToggleShakingMode();
                break;
            case 'Profile':
                if (onOpenAccountSwitcher) onOpenAccountSwitcher();
                break;
            case 'Communities':
                if (onOpenCommunitiesModal) onOpenCommunitiesModal();
                break;
            case 'Mod':
                if (onOpenModLibrary) onOpenModLibrary();
                break;
            case 'Settings':
                setShowQuickAppearance(true);
                break;
            default:
                break;
        }
    };

    const handleDragStart = (buttonId: string, clientY: number) => {
        setActiveDragButton(buttonId);
        dragStartYRef.current = clientY;
        setDragDeltaY(0);
        hasTriggeredDragRef.current = false;
    };

    useEffect(() => {
        if (!activeDragButton) return;

        const handleMove = (clientY: number) => {
            const diffY = clientY - dragStartYRef.current;
            const cappedY = Math.max(-80, Math.min(0, diffY));
            setDragDeltaY(cappedY);

            if (diffY <= -5) {
                if (settingsLongPressTimer.current) {
                    clearTimeout(settingsLongPressTimer.current);
                    settingsLongPressTimer.current = null;
                }
                if (homeLongPressTimer.current) {
                    clearTimeout(homeLongPressTimer.current);
                    homeLongPressTimer.current = null;
                }
                if (profileLongPressTimer.current) {
                    clearTimeout(profileLongPressTimer.current);
                    profileLongPressTimer.current = null;
                }
                if (communitiesLongPressTimer.current) {
                    clearTimeout(communitiesLongPressTimer.current);
                    communitiesLongPressTimer.current = null;
                }
                if (longPressTimer.current) {
                    clearTimeout(longPressTimer.current);
                    longPressTimer.current = null;
                }
            }
        };

        const handleEnd = () => {
            if (dragDeltaY <= -30) {
                hasTriggeredDragRef.current = true;
                triggerHoldAction(activeDragButton);

                if (activeDragButton === 'Home') wasHomeLongPress.current = true;
                if (activeDragButton === 'Profile') wasProfileLongPress.current = true;
                if (activeDragButton === 'Communities') wasCommunitiesLongPress.current = true;
                if (activeDragButton === 'Mod') wasLongPress.current = true;
                if (activeDragButton === 'Settings') wasSettingsLongPress.current = true;
            }
            setActiveDragButton(null);
            setDragDeltaY(0);
        };

        const onTouchMove = (e: TouchEvent) => {
            if (e.cancelable) {
                e.preventDefault();
            }
            handleMove(e.touches[0].clientY);
        };
        const onMouseMove = (e: MouseEvent) => {
            handleMove(e.clientY);
        };
        const onEnd = () => {
            handleEnd();
        };

        window.addEventListener('touchmove', onTouchMove, { passive: false });
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('touchend', onEnd);
        window.addEventListener('mouseup', onEnd);

        return () => {
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchend', onEnd);
            window.removeEventListener('mouseup', onEnd);
        };
    }, [activeDragButton, dragDeltaY]);

    useEffect(() => {
        if (!showQuickAppearance) return;
        const handleClickOutside = (e: MouseEvent) => {
            const targetButton = (e.target as HTMLElement).closest('button');
            if (targetButton && targetButton.getAttribute('title') === 'Settings') {
                return;
            }
            if (quickAppearanceRef.current && !quickAppearanceRef.current.contains(e.target as Node)) {
                setShowQuickAppearance(false);
            }
        };
        const timer = setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
        }, 100);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showQuickAppearance]);

    const handleUiScaleChange = (val: number) => {
        if (!currentUser || !auth.updateUserData) return;
        auth.updateUserData((prevData: any) => ({
            ...prevData,
            [currentUser]: { ...prevData[currentUser], uiScale: val }
        }));
        if (!isDemo) {
            updateUserOnServer(currentUser, { uiScale: val }).catch(console.error);
        }
    };

    const handleThemeColorChange = (color: string) => {
        if (!currentUser || !auth.updateUserData) return;
        auth.updateUserData((prevData: any) => ({
            ...prevData,
            [currentUser]: { ...prevData[currentUser], appThemeColor: color }
        }));
        if (!isDemo) {
            updateUserOnServer(currentUser, { appThemeColor: color }).catch(console.error);
        }
    };

    const handleTextColorChange = (color: string) => {
        if (!currentUser || !auth.updateUserData) return;
        auth.updateUserData((prevData: any) => ({
            ...prevData,
            [currentUser]: { ...prevData[currentUser], appTextColor: color }
        }));
        if (!isDemo) {
            updateUserOnServer(currentUser, { appTextColor: color }).catch(console.error);
        }
    };

    const handleOpacityChange = (val: number) => {
        if (!currentUser || !auth.updateUserData) return;
        auth.updateUserData((prevData: any) => ({
            ...prevData,
            [currentUser]: { ...prevData[currentUser], postBlurOpacity: val }
        }));
        if (!isDemo) {
            updateUserOnServer(currentUser, { postBlurOpacity: val }).catch(console.error);
        }
    };

    const handleEnforceThemeColorChange = (val: boolean) => {
        if (!currentUser || !auth.updateUserData) return;
        auth.updateUserData((prevData: any) => ({
            ...prevData,
            [currentUser]: { ...prevData[currentUser], enforceThemeColor: val }
        }));
        if (!isDemo) {
            updateUserOnServer(currentUser, { enforceThemeColor: val }).catch(console.error);
        }
    };

    const handleEnforceTextColorChange = (val: boolean) => {
        if (!currentUser || !auth.updateUserData) return;
        auth.updateUserData((prevData: any) => ({
            ...prevData,
            [currentUser]: { ...prevData[currentUser], enforceTextColor: val }
        }));
        if (!isDemo) {
            updateUserOnServer(currentUser, { enforceTextColor: val }).catch(console.error);
        }
    };

    const handleEnforceThemeOpacityChange = (val: boolean) => {
        if (!currentUser || !auth.updateUserData) return;
        auth.updateUserData((prevData: any) => ({
            ...prevData,
            [currentUser]: { ...prevData[currentUser], enforceThemeOpacity: val }
        }));
        if (!isDemo) {
            updateUserOnServer(currentUser, { enforceThemeOpacity: val }).catch(console.error);
        }
    };

    useEffect(() => {
        const handleScroll = (e: Event) => {
            let scrollTop = 0;
            if (e.target === document) {
                scrollTop = window.scrollY;
            } else {
                const el = e.target as HTMLElement;
                if (el.classList && (el.classList.contains('overflow-y-auto') || el.classList.contains('custom-scrollbar') || el.classList.contains('feed-scroll-container') || el.tagName === 'MAIN')) {
                    scrollTop = el.scrollTop;
                } else {
                    return;
                }
            }
            setIsScrolled((prev) => {
                if (scrollTop > 100 && !prev) return true;
                if (scrollTop <= 100 && prev) return false;
                return prev;
            });
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, []);



    useEffect(() => {
        const handleSpacebarToggle = (e: KeyboardEvent) => {
            if (e.code === 'Space' || e.key === ' ') {
                if (!activeAudioPost) return;

                const activeEl = document.activeElement as HTMLElement;
                if (
                    activeEl &&
                    (activeEl.tagName === 'INPUT' ||
                        activeEl.tagName === 'TEXTAREA' ||
                        activeEl.isContentEditable)
                ) {
                    return; // Let spacebar work normally when typing
                }

                // Prevent default scrolling behavior
                e.preventDefault();
                playAudioPost(activeAudioPost);
            }
        };

        window.addEventListener('keydown', handleSpacebarToggle);
        return () => window.removeEventListener('keydown', handleSpacebarToggle);
    }, [activeAudioPost, playAudioPost]);

    const handleNavClick = (targetPage: PageState, isMatch: boolean) => {
        if (wasLongPress.current) {
            wasLongPress.current = false;
            return;
        }
        if (isMatch && isScrolled) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            document.querySelectorAll('.overflow-y-auto, .custom-scrollbar, main, .feed-scroll-container').forEach(el => {
                el.scrollTo({ top: 0, behavior: 'smooth' });
            });
            setIsScrolled(false);
        } else {
            setPage(targetPage);
        }
    };
    const appIcons = usersData?.['goodlyfe']?.appIcons || {};

    const renderNavButton = (
        iconKey: string,
        targetPage: PageState,
        isSelected: boolean,
        IconComponent: React.ComponentType<any>,
        label: string,
        extraElement?: React.ReactNode,
        customOnClick?: () => void,
        customHandlers?: {
            onMouseDown?: () => void;
            onMouseUp?: () => void;
            onMouseLeave?: () => void;
            onTouchStart?: () => void;
            onTouchEnd?: () => void;
        }
    ) => {
        const isMedia = appIcons[iconKey]?.type === 'media';
        const onClickHandler = (e: React.MouseEvent) => {
            if (hasTriggeredDragRef.current) {
                e.stopPropagation();
                hasTriggeredDragRef.current = false;
                return;
            }
            if (iconKey === 'UserIcon' && wasProfileLongPress.current) {
                e.stopPropagation();
                wasProfileLongPress.current = false;
                return;
            }
            if (iconKey === 'HomeIcon' && wasHomeLongPress.current) {
                e.stopPropagation();
                wasHomeLongPress.current = false;
                return;
            }
            if (iconKey === 'UsersIcon' && wasCommunitiesLongPress.current) {
                e.stopPropagation();
                wasCommunitiesLongPress.current = false;
                return;
            }
            if (customOnClick) {
                customOnClick();
            } else {
                handleNavClick(targetPage, isSelected);
            }
        };

        const isDraggingThis = activeDragButton === label;
        const dragStyle = {
            transform: isDraggingThis ? `translateY(${dragDeltaY}px)` : 'none',
            transition: isDraggingThis ? 'none' : 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
        };

        if (isMedia) {
            return (
                <button
                    onClick={(e) => onClickHandler(e)}
                    {...customHandlers}
                    onMouseDown={(e) => {
                        customHandlers?.onMouseDown?.();
                        handleDragStart(label, e.clientY);
                    }}
                    onTouchStart={(e) => {
                        customHandlers?.onTouchStart?.();
                        handleDragStart(label, e.touches[0].clientY);
                    }}
                    className={`relative flex items-center justify-center rounded-full transition-all p-1.5 ${isSelected ? 'scale-110 z-10' : 'hover:bg-white/10 active:scale-95'}`}
                    style={{
                        color: 'var(--app-text-color)',
                        ...dragStyle
                    }}
                    title={label}
                >
                    <IconComponent
                        className="w-5 h-5 sm:w-5 sm:h-5"
                        isNavIcon={true}
                        isSelected={isSelected}
                    />
                    {extraElement}
                </button>
            );
        }

        const isHomeShaking = iconKey === 'HomeIcon' && isShakingMode;

        return (
            <button
                onClick={(e) => onClickHandler(e)}
                {...customHandlers}
                onMouseDown={(e) => {
                    customHandlers?.onMouseDown?.();
                    handleDragStart(label, e.clientY);
                }}
                onTouchStart={(e) => {
                    customHandlers?.onTouchStart?.();
                    handleDragStart(label, e.touches[0].clientY);
                }}
                className={`relative flex items-center justify-center p-2 sm:p-2.5 rounded-full transition-all ${isSelected ? 'bg-black/30 scale-110 z-10' : 'hover:bg-white/10 active:scale-95'}`}
                style={{
                    color: 'var(--app-text-color)',
                    ...dragStyle,
                    ...(isHomeShaking && {
                        boxShadow: `0 0 15px ${userThemeColor}`,
                        animation: 'pulse 1.5s infinite alternate',
                        backgroundColor: 'rgba(0,0,0,0.4)'
                    })
                }}
                title={label}
            >
                {isSelected && isScrolled && (iconKey === 'HomeIcon' || iconKey === 'UserIcon' || iconKey === 'UsersIcon') ? (
                    <ArrowUpIcon className="w-5 h-5 sm:w-5 sm:h-5" isNavIcon={true} isSelected={true} />
                ) : (
                    <IconComponent className="w-5 h-5 sm:w-5 sm:h-5" isNavIcon={true} isSelected={isSelected} />
                )}
                <span className="sr-only">{label}</span>
                {extraElement}
            </button>
        );
    };

    // --- Auto-Hide Inactivity Tracking ---
    const enableNavbarAutohide = currentUserData?.enableNavbarAutohide ?? false;

    const [isVisible, setIsVisible] = useState(true);
    const activityTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const resetActivityTimer = React.useCallback(() => {
        setIsVisible(true);
        if (activityTimeoutRef.current) {
            clearTimeout(activityTimeoutRef.current);
        }
        activityTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
        }, 3000);
    }, []);

    // Monitor global user activity to show/reset navbar timer
    useEffect(() => {
        resetActivityTimer();

        const handleSwipe = () => {
            resetActivityTimer();
        };

        const handleScreenTouch = () => {
            resetActivityTimer();
        };

        window.addEventListener('swipe-nav', handleSwipe);
        window.addEventListener('click', handleScreenTouch, { capture: true });

        return () => {
            if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
            window.removeEventListener('swipe-nav', handleSwipe);
            window.removeEventListener('click', handleScreenTouch, { capture: true });
        };
    }, [resetActivityTimer]);

    // Re-trigger visibility when page changes (button taps)
    useEffect(() => {
        resetActivityTimer();
    }, [page, resetActivityTimer]);

    return (
        <>
            {((isIos && canGoBack && onBack) || (page.type === 'profile')) && (
                <div className="fixed top-0 left-0 right-0 flex items-center justify-between z-[60] px-6 py-2 pt-[calc(0.5rem+env(safe-area-inset-top,0px))] pointer-events-none">
                    {isIos && canGoBack && onBack ? (
                        <button
                            onClick={onBack}
                            className="flex items-center gap-1 py-1.5 px-3 rounded-full bg-white/5 hover:bg-white/10 text-white/90 transition-all active:scale-95 group shadow-lg pointer-events-auto border border-white/10"
                        >
                            <ChevronLeftIcon className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                            {backButtonContent || <span className="text-[10px] font-bold uppercase tracking-wider">Back</span>}
                        </button>
                    ) : (
                        <div />
                    )}

                    {page.type === 'profile' && onTogglePeek && (
                        <button
                            onClick={onTogglePeek}
                            className="flex items-center gap-1 py-1.5 px-3 rounded-full bg-white/5 hover:bg-white/10 text-white/90 transition-all active:scale-95 group shadow-lg pointer-events-auto border border-white/10"
                            style={{
                                borderColor: isPeeking ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)',
                                backgroundColor: isPeeking ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255, 255, 255, 0.05)'
                            }}
                        >
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                                {isPeeking ? 'Show Posts' : 'Peek'}
                            </span>
                        </button>
                    )}
                </div>
            )}
            <header
                className="fixed bottom-0 left-0 right-0 flex flex-col justify-center items-center z-50 px-6 py-2 pb-[calc(1.2rem+var(--safe-area-bottom,0px))] pointer-events-none transition-all duration-500 ease-out gap-1.5"
                style={{
                    transform: (enableNavbarAutohide ? isVisible : true)
                        ? 'translate3d(0, 0, 0)'
                        : 'translate3d(0, calc(100% + 20px), 0)',
                    opacity: (enableNavbarAutohide ? isVisible : true) ? 1 : 0,
                    visibility: (enableNavbarAutohide ? isVisible : true) ? 'visible' : 'hidden'
                }}
            >
                {isShakingMode && postcardGap !== undefined && setPostcardGap && (() => {
                    const hexToRgb = (hex: string) => {
                        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                        return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
                    };

                    const getExactBg = (color: string, opacityVal: number) => {
                        const rgb = hexToRgb(color);
                        if (!rgb) return 'rgba(0, 0, 0, 0.75)';
                        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacityVal})`;
                    };

                    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;
                    const modalBg = getExactBg('#000000', Math.max(0.7, userOpacity));

                    return (
                        <div
                            ref={spacingModeRef}
                            className="pointer-events-auto w-[380px] max-w-[calc(100vw-2rem)] mb-2 z-50 rounded-[2.5rem] p-5 flex flex-col gap-3 items-center animate-fade-in shadow-2xl border border-white/10"
                            style={{
                                backgroundColor: modalBg,
                                color: userTextColor,
                                backdropFilter: 'blur(20px)'
                            }}
                        >
                            <span className="text-xs font-black uppercase tracking-widest" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                                ✨ Postcard Spacing Mode
                            </span>
                            <div className="flex flex-col items-center gap-2 w-full px-1">
                                <span className="text-[11px] font-extrabold tracking-widest uppercase" style={stickerTextStyle(0.18)}>
                                    Gap: <span style={{ color: userThemeColor }}>{Math.round(postcardGap)}px</span>
                                </span>
                                <input
                                    type="range"
                                    min="0"
                                    max="48"
                                    value={postcardGap}
                                    onChange={(e) => setPostcardGap(parseFloat(e.target.value))}
                                    className="w-full max-w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-black/30 focus:border-white/30 animate-pulse"
                                    style={{
                                        boxSizing: 'border-box',
                                        accentColor: userThemeColor
                                    }}
                                />
                            </div>
                            <span className="text-[9px] opacity-60 font-medium text-center leading-normal" style={stickerTextStyle(0.18)}>
                                Pinch in/out to adjust gaps, or use the slider above. Tap Home to exit.
                            </span>
                        </div>
                    );
                })()}

                {activeAudioPost && (() => {
                    const authorData = usersData[activeAudioPost.author];
                    const authorThemeColor = authorData?.appThemeColor || '#3b82f6';
                    const authorTextColor = authorData?.appTextColor || '#ffffff';
                    const authorOpacity = (authorData?.postBlurOpacity !== undefined ? authorData.postBlurOpacity : 50) / 100;

                    const hexToRgb = (hex: string) => {
                        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                        return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
                    };

                    const getExactBg = (color: string, opacityVal: number) => {
                        const rgb = hexToRgb(color);
                        if (!rgb) return 'rgba(255, 255, 255, 0.85)';
                        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacityVal})`;
                    };

                    const barBg = getExactBg(authorThemeColor, authorOpacity);
                    const media = (activeAudioPost.type === 'bump' || activeAudioPost.type === 'repost') ? (activeAudioPost.originalPost?.content.media) : activeAudioPost.content.media;
                    const firstAudio = media?.find((m: any) => m.type === 'audio' || m.type === 'voice');
                    const audioName = firstAudio?.name;
                    const titleText = `Now Playing - ${audioName || activeAudioPost.content?.text || (activeAudioPost.type === 'voice' ? 'Voice note' : 'Audio')}`;
                    return (
                        <div
                            onClick={() => setAudioPlayerOpen(true)}
                            className="pointer-events-auto text-[10px] font-bold px-3 py-1.5 rounded-full border border-black/10 shadow-lg cursor-pointer flex items-center gap-1.5 animate-fade-in transition-all active:scale-95 select-none"
                            style={{
                                backgroundColor: barBg,
                                color: authorTextColor,
                                borderColor: `${authorTextColor}20`,
                                ...stickerTextStyle(0.18)
                            }}
                        >
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    playAudioPost(activeAudioPost);
                                }}
                                className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-black/10 active:scale-90 transition-all flex-shrink-0"
                                style={{ color: authorTextColor }}
                                title={audioIsPlaying ? "Pause Playback" : "Resume Playback"}
                            >
                                {audioIsPlaying ? (
                                    <PauseIcon className="w-3 h-3" />
                                ) : (
                                    <PlayIcon className="w-3 h-3 ml-0.5" />
                                )}
                            </button>
                            <div className="overflow-hidden relative w-[140px] sm:w-[200px] flex items-center select-none" style={{ maskImage: 'linear-gradient(to right, transparent, black 8px, black 90%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 8px, black 90%, transparent)' }}>
                                <style>{`
                                    @keyframes marquee {
                                        0% { transform: translate3d(0, 0, 0); }
                                        100% { transform: translate3d(-50%, 0, 0); }
                                    }
                                `}</style>
                                <div
                                    className="whitespace-nowrap flex gap-8"
                                    style={{ animation: 'marquee 12s linear infinite' }}
                                >
                                    <span>{titleText}</span>
                                    <span>{titleText}</span>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {showQuickAppearance && (() => {
                    const hexToRgb = (hex: string) => {
                        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                        return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
                    };

                    const getExactBg = (color: string, opacityVal: number) => {
                        const rgb = hexToRgb(color);
                        if (!rgb) return 'rgba(0, 0, 0, 0.75)';
                        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacityVal})`;
                    };

                    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;
                    const modalBg = getExactBg('#000000', Math.max(0.75, userOpacity));

                    const localUiScale = currentUserData?.uiScale ?? 1.0;
                    const localThemeColor = currentUserData?.appThemeColor ?? '#161b22';
                    const localTextColor = currentUserData?.appTextColor ?? '#ffffff';
                    const localOpacityVal = currentUserData?.postBlurOpacity ?? 50;
                    const localEnforceThemeColor = currentUserData?.enforceThemeColor ?? false;
                    const localEnforceTextColor = currentUserData?.enforceTextColor ?? false;
                    const localEnforceThemeOpacity = currentUserData?.enforceThemeOpacity ?? false;

                    return (
                        <div
                            ref={quickAppearanceRef}
                            className="pointer-events-auto w-[380px] max-w-[calc(100vw-2rem)] mb-2 z-50 rounded-[2.5rem] p-5 flex flex-col gap-4 animate-fade-in shadow-2xl border border-white/10"
                            style={{
                                backgroundColor: modalBg,
                                color: userTextColor
                            }}
                        >
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-black uppercase tracking-widest" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                                    🎨 Quick Appearance
                                </span>
                                <button
                                    onClick={() => setShowQuickAppearance(false)}
                                    className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 bg-white/5 hover:bg-white/10 rounded-full border border-white/10 transition-all active:scale-95 text-white/90"
                                >
                                    Done
                                </button>
                            </div>

                            {/* Pick Your Size */}
                            <div className="bg-black/20 border border-white/5 rounded-2xl p-3.5 space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-bold uppercase" style={stickerTextStyle(0.18)}>Pick your size</span>
                                    <span className="text-[10px] font-black" style={{ color: userThemeColor }}>{Math.round(localUiScale * 100)}%</span>
                                </div>
                                <input
                                    type="range"
                                    min="0.5"
                                    max="1.5"
                                    step="0.05"
                                    value={localUiScale}
                                    onChange={(e) => handleUiScaleChange(parseFloat(e.target.value))}
                                    className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-black/30 accent-blue-500"
                                    style={{ accentColor: userThemeColor }}
                                />
                            </div>

                            {/* App Theme Color */}
                            <div className="bg-black/20 border border-white/5 rounded-2xl p-3.5 space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-bold uppercase block" style={stickerTextStyle(0.18)}>Theme Color</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[8px] font-black uppercase opacity-40 tracking-widest">Enforce</span>
                                        <button
                                            onClick={() => handleEnforceThemeColorChange(!localEnforceThemeColor)}
                                            className={`w-7 h-4 rounded-full transition-all relative ${localEnforceThemeColor ? 'bg-green-500' : 'bg-white/10'}`}
                                        >
                                            <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${localEnforceThemeColor ? 'right-0.5' : 'left-0.5'}`} />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: localThemeColor }}>
                                        <input
                                            type="color"
                                            value={localThemeColor}
                                            onChange={(e) => handleThemeColorChange(e.target.value)}
                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        value={localThemeColor}
                                        onChange={(e) => handleThemeColorChange(e.target.value)}
                                        className="flex-1 min-w-0 bg-black/30 focus:border-white/30 rounded-xl py-1.5 px-3 text-[11px] font-mono text-center border border-white/5 focus:outline-none"
                                        style={{ color: userTextColor }}
                                    />
                                    <div className="flex gap-1 flex-shrink-0">
                                        <button
                                            onClick={() => { copyToClipboard(localThemeColor); }}
                                            className="px-2 py-1.5 text-[9px] font-black uppercase bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 transition-all"
                                        >
                                            Copy
                                        </button>
                                        <button
                                            onClick={async () => {
                                                const text = await readFromClipboard();
                                                if (text && /^#[0-9a-fA-F]{6}$/.test(text)) {
                                                    handleThemeColorChange(text);
                                                }
                                            }}
                                            className="px-2 py-1.5 text-[9px] font-black uppercase bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 transition-all"
                                        >
                                            Paste
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* App Text Color */}
                            <div className="bg-black/20 border border-white/5 rounded-2xl p-3.5 space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-bold uppercase block" style={stickerTextStyle(0.18)}>Text Color</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[8px] font-black uppercase opacity-40 tracking-widest">Enforce</span>
                                        <button
                                            onClick={() => handleEnforceTextColorChange(!localEnforceTextColor)}
                                            className={`w-7 h-4 rounded-full transition-all relative ${localEnforceTextColor ? 'bg-green-500' : 'bg-white/10'}`}
                                        >
                                            <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${localEnforceTextColor ? 'right-0.5' : 'left-0.5'}`} />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: localTextColor }}>
                                        <input
                                            type="color"
                                            value={localTextColor}
                                            onChange={(e) => handleTextColorChange(e.target.value)}
                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        value={localTextColor}
                                        onChange={(e) => handleTextColorChange(e.target.value)}
                                        className="flex-1 min-w-0 bg-black/30 focus:border-white/30 rounded-xl py-1.5 px-3 text-[11px] font-mono text-center border border-white/5 focus:outline-none"
                                        style={{ color: userTextColor }}
                                    />
                                    <div className="flex gap-1 flex-shrink-0">
                                        <button
                                            onClick={() => { copyToClipboard(localTextColor); }}
                                            className="px-2 py-1.5 text-[9px] font-black uppercase bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 transition-all"
                                        >
                                            Copy
                                        </button>
                                        <button
                                            onClick={async () => {
                                                const text = await readFromClipboard();
                                                if (text && /^#[0-9a-fA-F]{6}$/.test(text)) {
                                                    handleTextColorChange(text);
                                                }
                                            }}
                                            className="px-2 py-1.5 text-[9px] font-black uppercase bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 transition-all"
                                        >
                                            Paste
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Transparency */}
                            <div className="bg-black/20 border border-white/5 rounded-2xl p-3.5 space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-bold uppercase" style={stickerTextStyle(0.18)}>Transparency</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[8px] font-black uppercase opacity-40 tracking-widest">Enforce</span>
                                        <button
                                            onClick={() => handleEnforceThemeOpacityChange(!localEnforceThemeOpacity)}
                                            className={`w-7 h-4 rounded-full transition-all relative ${localEnforceThemeOpacity ? 'bg-green-500' : 'bg-white/10'}`}
                                        >
                                            <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${localEnforceThemeOpacity ? 'right-0.5' : 'left-0.5'}`} />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-black" style={{ color: userThemeColor }}>{Math.round(localOpacityVal)}%</span>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={localOpacityVal}
                                    onChange={(e) => handleOpacityChange(parseInt(e.target.value))}
                                    className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-black/30 accent-blue-500"
                                    style={{ accentColor: userThemeColor }}
                                />
                            </div>
                        </div>
                    );
                })()}

                <nav className="flex flex-col items-center gap-1 rounded-3xl shadow-lg p-1 z-50 px-2 transition-transform duration-300 pointer-events-auto" style={{ backgroundColor: 'var(--app-theme-bg)' }}>
                    {isDemo && (
                        <div className="text-[10px] font-bold text-blue-400 uppercase tracking-widest pt-1 animate-pulse">
                            Play Around Mode
                        </div>
                    )}
                    <div className="flex items-center gap-1 sm:gap-1">
                        {renderNavButton(
                            'HomeIcon',
                            { type: 'home' },
                            page.type === 'home',
                            HomeIcon,
                            'Home',
                            undefined,
                            undefined,
                            {
                                onMouseDown: handleHomeTouchStart,
                                onMouseUp: handleHomeTouchEnd,
                                onMouseLeave: handleHomeTouchEnd,
                                onTouchStart: handleHomeTouchStart,
                                onTouchEnd: handleHomeTouchEnd,
                            }
                        )}
                        {(currentUserId || isDemo) && (
                            <>
                                {renderNavButton(
                                    'UserIcon',
                                    { type: 'profile', userId: currentUserId || 'guest' },
                                    page.type === 'profile' && page.userId === currentUserId,
                                    UserIcon,
                                    'Profile',
                                    undefined,
                                    undefined,
                                    {
                                        onMouseDown: handleProfileTouchStart,
                                        onMouseUp: handleProfileTouchEnd,
                                        onMouseLeave: handleProfileTouchEnd,
                                        onTouchStart: handleProfileTouchStart,
                                        onTouchEnd: handleProfileTouchEnd,
                                    }
                                )}
                                {renderNavButton(
                                    'UsersIcon',
                                    { type: 'communities' },
                                    ['communities', 'community_detail'].includes(page.type),
                                    UsersIcon,
                                    'Communities',
                                    undefined,
                                    undefined,
                                    {
                                        onMouseDown: handleCommunitiesTouchStart,
                                        onMouseUp: handleCommunitiesTouchEnd,
                                        onMouseLeave: handleCommunitiesTouchEnd,
                                        onTouchStart: handleCommunitiesTouchStart,
                                        onTouchEnd: handleCommunitiesTouchEnd,
                                    }
                                )}
                                {renderNavButton(
                                    'MailIcon',
                                    { type: 'messages' },
                                    page.type === 'messages',
                                    MailIcon,
                                    'Messages',
                                    hasUnreadMessages && (
                                        <span className="absolute top-1 right-1 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-gray-900/50"></span>
                                    ),
                                    () => setPage({ type: 'messages' })
                                )}
                                {/* Modular Apps Slot */}
                                {getActiveMods(activeMods, usersData).filter(m => m.id === pinnedModId || m.id === 'pimp_my_ride').map(mod => {
                                    const isSelected = page.type === 'mod' && page.modId === mod.id;
                                    return (
                                        <button
                                            key={mod.id}
                                            onClick={(e) => {
                                                if (hasTriggeredDragRef.current) {
                                                    e.stopPropagation();
                                                    hasTriggeredDragRef.current = false;
                                                    return;
                                                }
                                                if (wasLongPress.current) {
                                                    e.stopPropagation();
                                                    wasLongPress.current = false;
                                                    return;
                                                }
                                                handleNavClick({ type: 'mod', modId: mod.id }, isSelected);
                                            }}
                                            onMouseDown={(e) => {
                                                handleTouchStart();
                                                handleDragStart('Mod', e.clientY);
                                            }}
                                            onMouseUp={handleTouchEnd}
                                            onMouseLeave={handleTouchEnd}
                                            onTouchStart={(e) => {
                                                handleTouchStart();
                                                handleDragStart('Mod', e.touches[0].clientY);
                                            }}
                                            onTouchEnd={handleTouchEnd}
                                            className={`relative flex items-center justify-center rounded-full transition-all ${isSelected ? 'scale-110 z-10' : 'hover:bg-white/10 active:scale-95'}`}
                                            style={{
                                                color: 'var(--app-text-color)',
                                                transform: activeDragButton === 'Mod' ? `translateY(${dragDeltaY}px)` : 'none',
                                                transition: activeDragButton === 'Mod' ? 'none' : 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                                            }}
                                        >
                                            <div className={`relative w-7 h-7 sm:w-8 sm:h-8 rounded-full overflow-hidden transition-all duration-300 ${isSelected ? 'shadow-[0_0_15px_rgba(255,255,255,0.15)] ring-1 ring-white/20' : 'opacity-40 grayscale hover:opacity-100 hover:grayscale-0'}`}>
                                                {typeof mod.icon === 'string' ? (
                                                    <img src={mod.icon} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center p-1.5">
                                                        <mod.icon className="w-full h-full" />
                                                    </div>
                                                )}
                                            </div>
                                            {/* Hidden name for accessibility but keeping structure clean */}
                                            <span className="sr-only">{mod.name}</span>
                                        </button>
                                    );
                                })}
                            </>
                        )}
                        {!currentUserId && (
                            <button
                                onClick={() => {
                                    localStorage.removeItem('isDemo');
                                    window.location.pathname = '/';
                                }}
                                className="flex items-center gap-2 py-2 px-4 rounded-full text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                            >
                                Log In / Sign Up
                            </button>
                        )}
                        {(currentUserId || isDemo) && (
                            <>
                                {currentUserId && (
                                    <button
                                        onClick={(e) => {
                                            if (hasTriggeredDragRef.current) {
                                                e.stopPropagation();
                                                hasTriggeredDragRef.current = false;
                                                return;
                                            }
                                            if (wasSettingsLongPress.current) {
                                                e.stopPropagation();
                                                wasSettingsLongPress.current = false;
                                                return;
                                            }
                                            onSettingsClick();
                                        }}
                                        onMouseDown={(e) => {
                                            handleSettingsTouchStart();
                                            handleDragStart('Settings', e.clientY);
                                        }}
                                        onMouseUp={handleSettingsTouchEnd}
                                        onMouseLeave={handleSettingsTouchEnd}
                                        onTouchStart={(e) => {
                                            handleSettingsTouchStart();
                                            handleDragStart('Settings', e.touches[0].clientY);
                                        }}
                                        onTouchEnd={handleSettingsTouchEnd}
                                        className={`relative p-2 rounded-full transition-all duration-300 ${page.type === 'settings' || showQuickAppearance ? 'bg-black/30' : 'hover:bg-white/10'}`}
                                        style={{
                                            color: 'var(--app-text-color)',
                                            transform: activeDragButton === 'Settings' ? `translateY(${dragDeltaY}px)` : 'none',
                                            transition: activeDragButton === 'Settings' ? 'none' : 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                                        }}
                                        title="Settings"
                                    >
                                        <SettingsIcon className="w-5 h-5 sm:w-5 sm:h-5" />
                                    </button>
                                )}
                                {isDemo && (
                                    <button
                                        onClick={onLogout}
                                        className="flex items-center gap-2 py-1.5 px-3 rounded-full text-[10px] font-bold bg-white/20 text-white hover:bg-white/30 transition-colors border border-white/20"
                                    >
                                        <LogoutIcon className="w-3 h-3" />
                                        <span>LOGIN</span>
                                    </button>
                                )}
                                {activeVoiceData && (page.type !== 'community_detail' || page.query !== activeVoiceData.communityId) && (
                                    <button
                                        onClick={() => setPage({ type: 'community_detail', query: activeVoiceData.communityId, channelId: activeVoiceData.channelId } as any)}
                                        className="p-1 sm:p-2 rounded-full text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 transition-all duration-300 animate-pulse border border-blue-500/20"
                                        title="Return to Ongoing Call"
                                    >
                                        <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                )}
                                {hasUnreadNotifications && (
                                    <button
                                        onClick={() => setPage({ type: 'notifications' })}
                                        className="relative flex items-center gap-2 py-2 px-2 sm:px-3 rounded-full text-sm font-black uppercase transition-all bg-red-500/10 hover:bg-red-500/20 active:scale-95 animate-bounce-subtle"
                                        style={{ color: '#ff4b5b' }}
                                        title="New Notifications"
                                    >
                                        <BellIcon className="w-5 h-5 sm:w-5 sm:h-5" isNavIcon={true} isSelected={page.type === 'notifications'} />
                                        <span className="absolute -top-0.5 -right-0.5 block h-2 w-2 rounded-full bg-red-500 border border-black/50"></span>
                                    </button>
                                )}

                            </>
                        )}
                    </div>
                </nav>
            </header>
        </>
    );
};

